import {useState, useEffect, memo, useContext} from 'react';

import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {AddressViewValuesMap, LocationTypes} from '~/shared/consts/addressConsts';
import {getCurrentAddress as getCurrentAddressUtil} from '~/shared/services/addressHelper';
import {addressKeyFromParts} from '~/shared/utils/address';
import {isLocationAccessPermitted} from '~/shared/services/geocoder';
import {flexColumn} from '~/shared/theme/FlexLayout';

import {AddAddressActionButton, ButtonBar} from '../styled.addressDropdown';
import {AddressDropdownContext} from '../context';

import AddressList from './AddressList';

const Root = styled.div`
  height: 100%;
  ${flexColumn}
  ${({showNoAddressWarning}) => showNoAddressWarning && 'height: calc(100% - 40px);'}
`;

const Scroller = styled.div`
  overflow-y: auto;
  flex: 1;
  -webkit-overflow-scrolling: touch;
  min-height: 300px;
`;

const Main = ({
  customizedAddAddress,
  addresses,
  activeAddress,
  hideCurrentLocation,
  hideEditButton,
  eligibleToAddCompanyAddress,
  changeAddress,
  onViewChange,
  onSubmitEditedAddress,
}) => {
  const {t, currentLanguageKey} = getLocalizationService();

  const [myLocationAddress, setMyLocationAddress] = useState({data: null, loading: false});
  const [locationAccessEnabled, setLocationAccessEnabled] = useState(true);

  const {setLocalAddress} = useContext(AddressDropdownContext);

  useEffect(() => {
    (async () => {
      const isLocationAccessible = await isLocationAccessPermitted();
      setLocationAccessEnabled(isLocationAccessible);
    })();
  }, []);

  const onAddressChange = address => {
    changeAddress({address});
  };

  const onMyLocationAddressChange = address => {
    setLocalAddress(address);
    changeAddress({address});
  };

  const openAddAddress = () => {
    onViewChange(AddressViewValuesMap.ADD);
  };

  const openAddCompanyAddress = () => {
    onViewChange(AddressViewValuesMap.ADD_COMPANY_ADDRESS);
  };

  const onAddressEditClick = address => {
    onSubmitEditedAddress(address);
    onViewChange(address.addressId ? AddressViewValuesMap.EDIT : AddressViewValuesMap.EDIT_LOCAL);
  };

  const getCurrentAddress = () => {
    setMyLocationAddress({data: null, loading: true, error: null});

    getCurrentAddressUtil({currentLanguageKey}).then(
      async newLocationAddress => {
        let fullAddress = {};
        const {cityId, streetId, houseNumber} = newLocationAddress || {};
        if (cityId && streetId && houseNumber) {
          fullAddress = {
            locationType: LocationTypes.RESIDENTIAL,
            addressKey: addressKeyFromParts(newLocationAddress),
            ...newLocationAddress,
          };
        }
        setMyLocationAddress({data: fullAddress, loading: false});
      },
      err => {
        console.warn('Couldn\'t detect current address.', err); // eslint-disable-line no-console
        setMyLocationAddress({data: null, loading: false, error: t('we_could_not_locate_your_current_address')});
      },
    );
  };

  return (
    <Root>
      <Scroller>
        <AddressList
          hideCurrentLocation={hideCurrentLocation}
          myLocationAddress={myLocationAddress}
          onMyLocationAddressChange={onMyLocationAddressChange}
          getCurrentAddress={getCurrentAddress}
          hideEditButton={hideEditButton}
          onAddressEditClick={onAddressEditClick}
          locationAccessEnabled={locationAccessEnabled}
          addresses={addresses}
          onAddressChange={onAddressChange}
          activeAddress={activeAddress}
        />
      </Scroller>
      <ButtonBar>
        {eligibleToAddCompanyAddress && (
          <AddAddressActionButton onClick={openAddCompanyAddress} reverseColors>
            {t('add_company_address')}
          </AddAddressActionButton>
        )}
        <AddAddressActionButton data-test-id="navigateToNewAddressBtn" onClick={customizedAddAddress || openAddAddress}>
          {t('add_new_address')}
        </AddAddressActionButton>
      </ButtonBar>
    </Root>
  );
};

export default memo(Main);
